import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'ftp',
  version: '1.0.0',
  properties: [
    // === CREDENTIALS ===
    {
      key: 'credentialId',
      label: 'FTP Credentials',
      type: 'credential',
      required: true,
      typeOptions: {
        credentialType: 'ftp',
      },
      description: 'FTP/SFTP server credentials',
    },

    // === OPERATION SELECTOR ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'download',
      noDataExpression: true,
      options: [
        { value: 'delete', name: 'Delete', description: 'Delete a file or folder' },
        { value: 'download', name: 'Download', description: 'Download a file from the server' },
        { value: 'list', name: 'List', description: 'List files and folders' },
        { value: 'rename', name: 'Rename', description: 'Rename a file or folder' },
        { value: 'upload', name: 'Upload', description: 'Upload a file to the server' },
      ],
    },

    // === DIRECTORY PATH (for download - folder to download from) ===
    {
      key: 'directory',
      label: 'Directory',
      type: 'string',
      required: true,
      default: '/',
      placeholder: '/remote/path/',
      description: 'The remote directory to download from',
      displayOptions: {
        show: {
          operation: ['download'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getDirectories',
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Browse',
      },
    },
    // === FILENAME/PATTERN (for download - specific file or wildcard pattern) ===
    {
      key: 'filename',
      label: 'Filename',
      type: 'string',
      required: false,
      default: '',
      placeholder: '*.csv, test*, report.pdf',
      description: 'Filename or pattern to download. Supports wildcards (* for any characters). Leave empty to download all files.',
      displayOptions: {
        show: {
          operation: ['download'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'text',
        loadOptions: {
          method: 'getFilesInDirectory',
          dependsOn: ['directory'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Pattern',
        dataModeLabel: 'Browse',
      },
    },
    // === PATH (for delete - full file path or directory) ===
    {
      key: 'path',
      label: 'Path',
      type: 'string',
      required: true,
      default: '',
      placeholder: '/remote/path/file.txt',
      description: 'The remote file or folder path to delete',
      displayOptions: {
        show: {
          operation: ['delete'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'text',
        loadOptions: {
          method: 'getFiles',
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Browse',
      },
    },
    // === PATH (for upload - destination path including filename) ===
    {
      key: 'path',
      label: 'Destination Path',
      type: 'string',
      required: true,
      default: '/',
      placeholder: '/remote/path/filename.txt',
      description: 'The destination path including filename (e.g., /uploads/report.pdf)',
      displayOptions: {
        show: {
          operation: ['upload'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'text',
        loadOptions: {
          method: 'getDirectories',
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Browse',
      },
    },
    // === PATH (for list - directory path) ===
    {
      key: 'path',
      label: 'Path',
      type: 'string',
      required: true,
      default: '/',
      placeholder: '/remote/path/',
      description: 'The remote directory path to list',
      displayOptions: {
        show: {
          operation: ['list'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getDirectories',
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Browse',
      },
    },
    // === FILENAME PATTERN (for list - optional filter) ===
    {
      key: 'filenamePattern',
      label: 'Filename Filter',
      type: 'string',
      required: false,
      default: '',
      placeholder: '*.csv, test*, *.pdf',
      description: 'Optional: Filter results by filename pattern. Supports wildcards (* for any characters).',
      displayOptions: {
        show: {
          operation: ['list'],
        },
      },
    },

    // === DELETE OPTIONS ===
    {
      key: 'deleteOptions.folder',
      label: 'Delete Folder',
      type: 'boolean',
      default: false,
      description: 'Enable to delete folders (otherwise only files can be deleted)',
      displayOptions: {
        show: {
          operation: ['delete'],
        },
      },
    },
    {
      key: 'deleteOptions.recursive',
      label: 'Recursive',
      type: 'boolean',
      default: false,
      description: 'Delete folder contents recursively',
      displayOptions: {
        show: {
          operation: ['delete'],
          'deleteOptions.folder': [true],
        },
      },
    },

    // === DOWNLOAD OPTIONS ===
    {
      key: 'downloadOptions.dataPropertyName',
      label: 'Binary Property',
      type: 'string',
      default: 'data',
      description: 'Name of the binary property to store the downloaded file',
      displayOptions: {
        show: {
          operation: ['download'],
        },
      },
    },
    {
      key: 'downloadOptions.decodeTextContent',
      label: 'Decode Text Content',
      type: 'boolean',
      default: false,
      description: 'Automatically decode text files (CSV, JSON, TXT, XML) to readable text. When enabled, returns "content" as text in addition to binary data.',
      displayOptions: {
        show: {
          operation: ['download'],
        },
      },
    },
    {
      key: 'downloadOptions.parseJsonContent',
      label: 'Parse JSON Content',
      type: 'boolean',
      default: false,
      description: 'Automatically parse JSON files into an object/array. When enabled, returns "json" as the parsed data. This is faster than using a Code node for large JSON files.',
      displayOptions: {
        show: {
          operation: ['download'],
          'downloadOptions.decodeTextContent': [true],
        },
      },
    },

    // === LIST OPTIONS ===
    {
      key: 'listOptions.recursive',
      label: 'Recursive',
      type: 'boolean',
      default: false,
      description: 'List all subdirectories recursively',
      displayOptions: {
        show: {
          operation: ['list'],
        },
      },
    },

    // === RENAME OPTIONS ===
    {
      key: 'renameOptions.oldPath',
      label: 'Old Path',
      type: 'string',
      required: true,
      default: '',
      placeholder: '/remote/old-file.txt',
      description: 'The current path of the file or folder',
      displayOptions: {
        show: {
          operation: ['rename'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'text',
        loadOptions: {
          method: 'getFiles',
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Browse',
      },
    },
    {
      key: 'renameOptions.newPath',
      label: 'New Path',
      type: 'string',
      required: true,
      default: '',
      placeholder: '/remote/new-file.txt',
      description: 'The new path for the file or folder',
      displayOptions: {
        show: {
          operation: ['rename'],
        },
      },
    },
    {
      key: 'renameOptions.createDirectories',
      label: 'Create Directories',
      type: 'boolean',
      default: false,
      description: "Create destination directories if they don't exist",
      displayOptions: {
        show: {
          operation: ['rename'],
        },
      },
    },

    // === UPLOAD OPTIONS ===
    {
      key: 'uploadOptions.binaryFile',
      label: 'Binary File',
      type: 'boolean',
      default: true,
      description: 'Upload a binary file (disable to upload text content instead)',
      displayOptions: {
        show: {
          operation: ['upload'],
        },
      },
    },
    {
      key: 'uploadOptions.binaryPropertyName',
      label: 'Binary Property',
      type: 'string',
      required: true,
      default: 'data',
      description: 'Name of the binary property containing the file to upload',
      displayOptions: {
        show: {
          operation: ['upload'],
          'uploadOptions.binaryFile': [true],
        },
      },
    },
    {
      key: 'uploadOptions.textContent',
      label: 'Text Content',
      type: 'text',
      required: true,
      default: '',
      placeholder: 'Text content to upload...',
      description: 'The text content to upload to the file',
      typeOptions: {
        rows: 5,
      },
      displayOptions: {
        show: {
          operation: ['upload'],
          'uploadOptions.binaryFile': [false],
        },
      },
    },
  ],
};

export default schema;
